// Escalation Management page.
//
// Filter sidebar + paginated list + detail drawer with approve/deny flow.
// Mirrors the FailureTrackingPage shape. Default filter on first load is
// Status=Pending so executives see action items first.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUpCircle, Download } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import { escalationsApi } from "@/api/dealFlow/client";
import {
  pausePollingOnSessionExpiry,
  retryUnlessSessionExpired,
} from "@/api/polling";
import { escalationKeys } from "@/hooks/dealFlow/escalationKeys";
import { dealKeys } from "@/hooks/deals/useDeals";
import type {
  CriteriaLane,
  EscalationListQuery,
  EscalationRecordDto,
  EscalationStatus,
} from "@/api/dealFlow/types";
import { EscalationFilterSidebar } from "./escalations/EscalationFilterSidebar";
import { EscalationList } from "./escalations/EscalationList";
import { EscalationDetailDrawer } from "./escalations/EscalationDetailDrawer";

const LANES: CriteriaLane[] = ["A1", "A2", "A3", "A4"];
const STATUSES: EscalationStatus[] = ["Pending", "Approved", "Denied"];

function parseStatus(v: string | null): EscalationStatus | undefined {
  return STATUSES.find((s) => s === v);
}
function parseLane(v: string | null): CriteriaLane | undefined {
  return LANES.find((l) => l === v);
}

export function EscalationsPage() {
  const { hasPermission } = useAuth();
  const canResolve = hasPermission(DEAL_FLOW_PERMISSIONS.ResolveEscalation);
  const qc = useQueryClient();

  const [params, setParams] = useSearchParams();

  // Default to Pending so executives see action items first — applied at
  // parse time, NOT via a post-mount URL rewrite (the old effect re-keyed
  // the list query after the first render, firing two overlapping list
  // requests on every page load). A cleared filter is encoded explicitly as
  // status=all so it survives the absent-param default.
  const rawStatus = params.get("status");
  const status = rawStatus === null ? ("Pending" as EscalationStatus) : parseStatus(rawStatus);
  const lane = parseLane(params.get("lane"));
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const page = Math.max(1, Number(params.get("page") ?? "1"));
  const pageSize = Math.min(100, Math.max(1, Number(params.get("pageSize") ?? "25")));
  const sort = params.get("sort") ?? "escalatedAt:desc";

  const [openId, setOpenId] = useState<string | null>(null);
  const [csvCooldownUntil, setCsvCooldownUntil] = useState<number | null>(null);

  const query: EscalationListQuery = useMemo(
    () => ({ status, lane, from, to, page, pageSize, sort }),
    [status, lane, from, to, page, pageSize, sort],
  );

  const listQ = useQuery({
    queryKey: escalationKeys.list(query),
    queryFn: () => escalationsApi.list(query),
    // Live triage queue — but stop polling once the session expires so an
    // unattended tab doesn't 401 every 30s indefinitely.
    refetchInterval: pausePollingOnSessionExpiry(30_000),
    retry: retryUnlessSessionExpired,
    // Opt out of the 5-minute global staleTime.
    staleTime: 30_000,
  });

  const detailQ = useQuery({
    queryKey: escalationKeys.detail(openId),
    queryFn: () => (openId ? escalationsApi.get(openId) : Promise.resolve(null)),
    enabled: !!openId,
  });

  // Resolving an escalation mutates the EscalationRecord server-side (a
  // denied certification escalation also declines the certification); the
  // originating tracked item keeps its status and shows the outcome. The
  // deal-detail page's per-deal queries (intake checklist, document requests,
  // etc. — all nested under dealKeys.detail) must be invalidated so the
  // TrackedItemRow escalation chip re-renders without a manual refresh.
  const onResolved = useCallback(
    (record: EscalationRecordDto) => {
      qc.invalidateQueries({ queryKey: escalationKeys.all });
      qc.invalidateQueries({ queryKey: dealKeys.detail(record.dealId) });
      setOpenId(null);
    },
    [qc],
  );

  const approveM = useMutation({
    mutationFn: (body: { resolutionNotes: string; resolutionDirective: string }) =>
      escalationsApi.approve(openId!, body),
    onSuccess: onResolved,
  });
  const denyM = useMutation({
    mutationFn: (body: { resolutionNotes: string; resolutionDirective: string }) =>
      escalationsApi.deny(openId!, body),
    onSuccess: onResolved,
  });

  const setFilter = useCallback(
    (next: Partial<Record<string, string | null>>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === undefined || v === "") merged.delete(k);
        else merged.set(k, v);
      }
      if (Object.keys(next).some((k) => k !== "page")) merged.set("page", "1");
      setParams(merged, { replace: false });
    },
    [params, setParams],
  );

  const handleExportCsv = useCallback(() => {
    const now = Date.now();
    if (csvCooldownUntil && now < csvCooldownUntil) return;
    const url = escalationsApi.exportCsvUrl(query);
    window.open(url, "_blank");
    setCsvCooldownUntil(now + 60_000);
  }, [csvCooldownUntil, query]);

  useEffect(() => {
    if (!csvCooldownUntil) return;
    const remaining = csvCooldownUntil - Date.now();
    if (remaining <= 0) {
      setCsvCooldownUntil(null);
      return;
    }
    const t = setTimeout(() => setCsvCooldownUntil(null), remaining);
    return () => clearTimeout(t);
  }, [csvCooldownUntil]);

  const csvDisabled = !!csvCooldownUntil && Date.now() < csvCooldownUntil;

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Flow"
        title="Escalations"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCsv}
            disabled={csvDisabled}
            title={csvDisabled ? "You exported in the last 60 seconds." : "Download filtered escalations as CSV"}
            aria-label="Export filtered escalations as CSV"
          >
            <Download size={14} strokeWidth={1.5} aria-hidden />
            <span className="ml-space-1">
              {csvDisabled ? "Exported recently" : "Export CSV"}
            </span>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-4">
        <aside className="lg:col-span-3">
          <EscalationFilterSidebar
            status={status ?? null}
            lane={lane ?? null}
            from={from ?? ""}
            to={to ?? ""}
            onChange={setFilter}
          />
        </aside>

        <main className="lg:col-span-9">
          <Card className="p-space-4">
            {listQ.isLoading ? (
              <div role="status" aria-live="polite" className="text-gray-mid">
                Loading escalations…
              </div>
            ) : listQ.isError ? (
              <div role="alert" className="flex items-center gap-space-2 text-status-error">
                <ArrowUpCircle size={18} aria-hidden />
                <span>Failed to load escalations.</span>
              </div>
            ) : (
              <EscalationList
                page={listQ.data ?? { items: [], page: 1, pageSize, totalCount: 0 }}
                sort={sort}
                onSortChange={(s) => setFilter({ sort: s })}
                onPageChange={(p) => setFilter({ page: String(p) })}
                onPageSizeChange={(s) => setFilter({ pageSize: String(s) })}
                onOpen={(id) => setOpenId(id)}
              />
            )}

            {listQ.data?.items.length === 0 && !listQ.isLoading && (
              <div className="mt-space-3 text-gray-mid">
                <p>No escalations match your filters.</p>
                <button
                  type="button"
                  className="text-gold underline mt-space-2"
                  onClick={() =>
                    setFilter({
                      status: null,
                      lane: null,
                      from: null,
                      to: null,
                      page: "1",
                    })
                  }
                >
                  Clear filters
                </button>
              </div>
            )}
          </Card>
        </main>
      </div>

      <EscalationDetailDrawer
        open={!!openId}
        detail={detailQ.data ?? null}
        isLoading={detailQ.isLoading}
        canResolve={canResolve}
        onClose={() => setOpenId(null)}
        onApprove={(body) => approveM.mutateAsync(body)}
        onDeny={(body) => denyM.mutateAsync(body)}
        isSubmitting={approveM.isPending || denyM.isPending}
      />

      <span aria-live="polite" className="sr-only">
        {listQ.data
          ? `${listQ.data.totalCount} escalations match the current filters.`
          : ""}
      </span>

      <span className="sr-only">
        <Badge status="gold" /> Pending · <Badge status="finalized" /> Approved · <Badge status="error" /> Denied
      </span>
    </div>
  );
}
